"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import { getSiteContent } from "@/content/site";
import {
  COOKIE_PREFS_OPEN_EVENT,
  applyGpcToConsentStore,
  isGpcEnabled,
  readCookieConsent,
  writeCookieConsent,
  type CookieConsentCategories,
} from "@/lib/cookie-consent";
import { useLocale } from "@/lib/i18n/provider";
import { localizePath } from "@/lib/i18n/routing";

/**
 * Single cookie consent path for the marketing site.
 * Termly is retired — this banner is the only consent UI.
 * GPC auto-opts-out of analytics/marketing via the consent store (DEF-US-02).
 */
export default function CookieConsentBanner() {
  const { locale } = useLocale();
  const { cookieConsent: copy } = getSiteContent(locale);
  const [show, setShow] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [gpc, setGpc] = useState(false);
  const [prefs, setPrefs] = useState<CookieConsentCategories>({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const signalOn = isGpcEnabled();
    setGpc(signalOn);
    applyGpcToConsentStore();
    const stored = readCookieConsent();
    if (!stored) {
      setShow(true);
      return;
    }
    setPrefs(stored);
    writeCookieConsent(stored);
  }, []);

  useEffect(() => {
    const openPrefs = () => {
      const signalOn = isGpcEnabled();
      setGpc(signalOn);
      applyGpcToConsentStore();
      const stored = readCookieConsent();
      if (stored) setPrefs(stored);
      setShow(true);
      setShowPrefs(true);
    };
    window.addEventListener(COOKIE_PREFS_OPEN_EVENT, openPrefs);
    return () => window.removeEventListener(COOKIE_PREFS_OPEN_EVENT, openPrefs);
  }, []);

  if (!show) return null;

  const acceptAll = () => {
    const all: CookieConsentCategories = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    writeCookieConsent(all);
    setShow(false);
    setShowPrefs(false);
  };
  const rejectAll = () => {
    const none: CookieConsentCategories = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    writeCookieConsent(none);
    setShow(false);
    setShowPrefs(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={copy.ariaLabel}
      className="fixed inset-x-0 bottom-0 z-[9999] border-t border-[var(--color-surface-border)] bg-[var(--color-bg-raised)] px-5 py-4 text-[var(--color-text-primary)] shadow-[var(--shadow-elevated)]"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4">
        <p className="max-w-2xl text-sm leading-relaxed text-[var(--color-text-secondary)]">
          {copy.body}{" "}
          <Link
            href={localizePath("/legal/cookie-policy", locale)}
            className="text-[var(--color-accent-base)] underline underline-offset-2"
          >
            {copy.policyLinkLabel}
          </Link>
          {gpc ? (
            <>
              {" "}
              {copy.gpcNotice}{" "}
              <Link
                href={localizePath("/legal/do-not-sell", locale)}
                className="text-[var(--color-accent-base)] underline underline-offset-2"
              >
                {copy.gpcNoticeLinkLabel}
              </Link>
              .
            </>
          ) : null}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={rejectAll}
            className="rounded-full border border-[var(--color-surface-border)] px-4 py-2 text-sm font-semibold text-[var(--color-text-primary)]"
          >
            {copy.rejectAll}
          </button>
          <button
            type="button"
            onClick={() => setShowPrefs((v) => !v)}
            className="rounded-full border border-[var(--color-surface-border)] px-4 py-2 text-sm font-semibold text-[var(--color-text-primary)]"
          >
            {copy.preferences}
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className="rounded-full bg-[var(--color-accent-base)] px-4 py-2 text-sm font-semibold text-[var(--color-accent-contrast)]"
          >
            {copy.acceptAll}
          </button>
        </div>
      </div>
      {showPrefs ? (
        <div className="mx-auto mt-3 flex max-w-5xl flex-wrap items-center gap-4 text-sm text-[var(--color-text-secondary)]">
          <label>
            <input type="checkbox" checked disabled /> {copy.necessary}
          </label>
          <label>
            <input
              type="checkbox"
              checked={prefs.analytics}
              disabled={gpc}
              onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
            />{" "}
            {copy.analytics}
          </label>
          <label>
            <input
              type="checkbox"
              checked={prefs.marketing}
              disabled={gpc}
              onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
            />{" "}
            {copy.marketing}
          </label>
          <button
            type="button"
            onClick={() => {
              writeCookieConsent(prefs);
              setShow(false);
              setShowPrefs(false);
            }}
            className="rounded-full bg-[var(--color-accent-base)] px-4 py-2 text-sm font-semibold text-[var(--color-accent-contrast)]"
          >
            {copy.save}
          </button>
        </div>
      ) : null}
    </div>
  );
}
